/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import {Channels, ChannelState} from '@phenixrts/sdk';
import {Component, createRef} from 'preact';
import PropTypes from 'prop-types';
import UserDataStore from '../../../shared/userDataStore';
import {NoSignalScreen, AccessDeniedScreen, LoadingScreen} from '../Overlays';
import {FullScreenMessage} from '../../../shared/components';
import {ChunkedStreaming, ParentWindowNotifier} from '../../services';
import {PlaybackControlsBuilder} from '../PlaybackControls/PlaybackControlsBuilder';
import styles from './ChannelViewStyles.css';

const getVideoElementStyleClasses = (channelState) => {
  const videoClasses = [styles.video];

  if (channelState !== ChannelState.Playing) {
    videoClasses.push(styles.displayNone);
  }

  return videoClasses.join(' ');
};

const capabilityStreaming = 'streaming';
const supportsParentMessaging = window.parent?.postMessage !== undefined;
const isEmbedded = supportsParentMessaging && window.parent !== window;
const isChunkedStream = (decodedToken) => decodedToken.capabilities?.includes(capabilityStreaming);
const controlsVisibilityWhenNotMutedDurationInMilliseconds = 5000;
const controlsVisibilityWhenMutedDurationInMilliseconds = 120000;

export default class ChannelView extends Component {
  constructor(props) {
    super(props);
    this._disposables = [];
    this._channelRef = createRef();
    this._videoElementRef = createRef();
    this._controlsHideTimeoutId = undefined;
    this._controlsVisibilityDurationInMilliseconds = controlsVisibilityWhenMutedDurationInMilliseconds;

    this.state = {
      channelExists: null,
      channelState: ChannelState.Offline,
      areControlsShown: true,
      isFullscreen: false
    };

    this._rememberAudioState = this._rememberAudioState.bind(this);
  }

  componentDidMount() {
    const {subscriber, environment} = this.props;
    const {token, decodedToken, chunkedStreaming} = subscriber;

    if (!token) {
      return;
    }

    if (isChunkedStream(decodedToken)) {
      this._canAudioStateBeChanged = true;
      ChunkedStreaming.initializePhenixSDKForChunkedStreaming({
        useShaka: chunkedStreaming.useShaka,
        canPlayHls: environment.canPlayHls
      });
    }

    this._setupFullscreenListeners();
    this._setupAudioStateListener();
    this._startControlsHideTimer();
    this._createChannel();
  }

  shouldComponentUpdate(nextProps, nextState) {
    const shouldUpdate = Object.entries(nextState).reduce((shouldUpdate, [stateKey, nextStateValue]) => {
      return shouldUpdate || this.state[stateKey] !== nextStateValue;
    }, false);

    if (nextState.channelExists === false) {
      while (this._disposables.length) {
        this._disposables.pop().dispose();
      }

      this._channelRef.current?.dispose();
      this._removeAudioStateListener();
      this._removeFullscreenListeners();
    }

    return shouldUpdate;
  }

  componentWillUnmount() {
    while (this._disposables.length) {
      this._disposables.pop().dispose();
    }

    clearTimeout(this._controlsHideTimeoutId);
    this._removeAudioStateListener();
    this._removeFullscreenListeners();
    this._channelRef.current?.dispose();
  }

  _setupAudioStateListener() {
    this._videoElementRef.current.addEventListener('volumechange', this._rememberAudioState);
  }

  _removeAudioStateListener() {
    this._videoElementRef.current.removeEventListener('volumechange', this._rememberAudioState);
  }

  _setupFullscreenListeners() {
    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', this._handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this._handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this._handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', this._handleFullscreenChange);
  }

  _removeFullscreenListeners() {
    document.removeEventListener('fullscreenchange', this._handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this._handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this._handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this._handleFullscreenChange);
  }

  _handleFullscreenChange = () => {
    const isFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );

    this.setState({isFullscreen});
  };

  _startControlsHideTimer = () => {
    clearTimeout(this._controlsHideTimeoutId);
    this._controlsHideTimeoutId = setTimeout(() => {
      this.setState({areControlsShown: false});
    }, this._controlsVisibilityDurationInMilliseconds);
  };

  _showControls = () => {
    this.setState({areControlsShown: true});
    this._startControlsHideTimer();
  };

  _handleKeyDown = () => {
    this._showControls();
  };

  _rememberAudioState() {
    const {storeLocalStateKey} = this.props.subscriber;

    if (storeLocalStateKey) {
      const audioState = this._videoElementRef.current?.muted ? 'disabled' : 'enabled';

      UserDataStore.put('Audio:' + storeLocalStateKey, audioState);
    }

    this._controlsVisibilityDurationInMilliseconds = this._videoElementRef.current?.muted ?
      controlsVisibilityWhenMutedDurationInMilliseconds :
      controlsVisibilityWhenNotMutedDurationInMilliseconds;

    this._startControlsHideTimer();
  }

  _createChannel() {
    if (this._channelRef.current) {
      return;
    }

    if (!this.props.subscriber.token || !this._videoElementRef.current) {
      return;
    }

    const channel = this._channelRef.current = Channels.createChannel({
      token: this.props.subscriber.token,
      videoElement: this._videoElementRef.current
    });
    const channelStateObserver = state => this.setState({channelState: state});
    const mediaStreamObserver = mediaStream => {
      if (!mediaStream) {
        return;
      }

      this._canAudioStateBeChanged = mediaStream.getAudioTracks()?.[0]?.enabled ?? false;
    };

    const channelExistsObserver = exists => this.setState({channelExists: exists});

    this._disposables.push(channel.exists.subscribe(channelExistsObserver));
    this._disposables.push(channel.state.subscribe(channelStateObserver));
    this._disposables.push(channel.mediaStream.subscribe(mediaStreamObserver));

    if (isEmbedded) {
      const {channelId} = this.props.subscriber;

      this._disposables.push(new ParentWindowNotifier({
        channelId,
        channel
      }));
    }

    this._disposables.push(channel);
  }

  render() {
    const {channelExists, channelState, areControlsShown, isFullscreen} = this.state;

    // Note: Explicity checking for channelExists === false here is intentional.
    // channelExists === null indicates JsSDK has not determined the existence of the channel yet.
    if (channelExists === false) {
      return <FullScreenMessage messages={['Channel does not exist.']} />;
    }

    const {muted} = this.props.audio;
    const renderOverlay = channelState => {
      switch (channelState) {
        case ChannelState.Offline:
        case ChannelState.StandBy:
          return <NoSignalScreen />;

        case ChannelState.Starting:
        case ChannelState.Initializing:
        case ChannelState.Reconnecting:
          return <LoadingScreen />;

        case ChannelState.Unauthorized:
        case ChannelState.GeoBlocked:
        case ChannelState.GeoRestricted:
          return <AccessDeniedScreen />;

        case ChannelState.Error:
          return <FullScreenMessage messages={['Error']} />;

        case ChannelState.ConnectionError:
          return <FullScreenMessage messages={['Connection Error']} />;

        default:
          return null;
      }
    };

    const playbackControlsBuilder = new PlaybackControlsBuilder({
      videoElementRef: this._videoElementRef,
      forceUpdate: () => this.forceUpdate(),
      canAudioStateBeChanged: this._canAudioStateBeChanged,
      areControlsShown,
      showControls: this._showControls
    })
      .withAudioControls()
      .withFullscreenControls(isFullscreen);

    if (this._videoElementRef.current?.paused) {
      playbackControlsBuilder.withPlayButton();
    }

    return (
      <div
        className={styles.videoContainer}
        onMouseEnter={this._showControls}
        onMouseMove={this._showControls}
        onMouseLeave={this._startControlsHideTimer}
        onKeyDown={this._handleKeyDown}
        tabIndex={0}
      >
        <div id="videoContainer" className={styles.videoContainer}>
          {renderOverlay(channelState)}
          <video
            ref={this._videoElementRef}
            className={getVideoElementStyleClasses(channelState)}
            autoPlay={true}
            muted={muted}
            playsInline={true}
            controls={false}
          />
          {playbackControlsBuilder.build()}
        </div>
      </div>
    );
  }
}

ChannelView.propTypes = {
  audio: PropTypes.object.isRequired,
  subscriber: PropTypes.object.isRequired,
  environment: PropTypes.object.isRequired
};